import "./CountertopUniversityForm.scss"
import { CountertopUniversityDetail } from "../types/CountertopUniversityDetail";
import { useEffect, useRef, useState } from "react";
import CountertopUniversityQuiz from "./CountertopUniversityQuiz";
import { showErrorMessage } from "../../../../utils/messages";
import { useLocation, useNavigate } from 'react-router';
import Player from '@vimeo/player';
import { useSaveCountertopUniversityVideo } from "../api/save-countertop-university-video";
import Button from "devextreme-react/button";
import LoadIndicator from "devextreme-react/load-indicator";
import CountertopUniversityCertificateViewerContent from "./CountertopUniversityCertificateViewerContent";
import { formatShortDateShortTime } from "../../../../utils/date-utils";
import { useSaveCountertopUniversitySurveyAttempt } from "../api/save-countertop-university-survey-attempt";

export default function CountertopUniversityLeaf({ item, onVideoComplete, onQuizComplete }: { item: CountertopUniversityDetail, onVideoComplete: () => void, onQuizComplete?: () => void }) {
    const navigate = useNavigate();
    const vimeoPlayerRef = useRef(null);

    const [isSurveyLoading, setSurveyLoading] = useState(false);
    const [isSurveyStarted, setSurveyStarted] = useState(false);
    const [videoDone, setVideoDone] = useState(item.videoDone);

    const { mutate } = useSaveCountertopUniversityVideo(
        item.countertopUniversityID,
        1,
        () => {
            setVideoDone(true);
            onVideoComplete();
        },
        (error) => showErrorMessage(error)
    );

    useEffect(() => {
        if (item.videoUrl && vimeoPlayerRef.current !== null) {
            try {
                const iframe = (vimeoPlayerRef.current as any).querySelector('iframe');
                const player = new Player(iframe);
                player.on("ended", () => mutate());
            } catch (e) {
                console.log(e);
            }
        }
    }, [item.videoUrl]);

    useEffect(() => {
        setVideoDone(item.videoDone);
    }, [item.countertopUniversityID]);

    const saveSurveyAttempt = useSaveCountertopUniversitySurveyAttempt(
        item.countertopUniversityID,
        () => {},
        (error) => showErrorMessage(error)
    );

    const handleStartSurvey = () => {
        if (!item.surveyLink) return;

        saveSurveyAttempt.mutate();

        setSurveyLoading(true);

        try 
        {
            let startIndex = item.surveyLink.indexOf('src="') + 5;
            let endIndex = item.surveyLink.indexOf('.js"') + 3;
            let src = item.surveyLink.substring(startIndex, endIndex).trim();

            if (!src) {
                setSurveyLoading(false);
                return;
            }

            let el = document.createElement("script");
            el.src = src;
            el.async = true;

            el.onload = () => {
                console.log("Survey script loaded.");

                setTimeout(() => {
                    const iframe = document.getElementsByTagName("iframe");
                    if (iframe.length > 0) {
                        iframe[0].style.maxHeight = "68vh";
                    }

                    setSurveyLoading(false);
                    setSurveyStarted(true);
                }, 1000);
            };

            el.onerror = (e: any) => {
                setSurveyLoading(false);
                showErrorMessage(`Failed to load survey. Error details: ${e.message}`);
            };

            document.body.appendChild(el);
        } catch (error) {
            console.error(error);
            setSurveyLoading(false);
        }
    };

    return (
        <div className='dx-card countertop-university-leaf-block'>
            {item.videoUrl && (
                <div className="countertop-university-leaf-video-block">
                    <div className="countertop-university-leaf-left">
                        <div
                            className="countertop-university-leaf-video"
                            ref={vimeoPlayerRef}
                            dangerouslySetInnerHTML={{
                                __html: item.videoUrl.replace('width:80%;height:80%', 'width:100%;height:100%') || ''
                            }}
                        />
                        <div className="countertop-university-leaf-content">
                            <div className="countertop-university-leaf-content-title">
                                <span className="countertop-university-leaf-content-span-title">{item.title}</span>
                                <Button
                                    icon=""
                                    visible={!!item.nextVideoUrl}
                                    text="Next Video"
                                    type='normal'
                                    width={'120px'}
                                    onClick={() => navigate(item.nextVideoUrl)}
                                />
                            </div>
                            <span className="countertop-university-leaf-content-descriptiontitle">{item.descriptionTitle}</span>
                            <span className="countertop-university-leaf-content-description">{item.description}</span>
                        </div>
                    </div>

                    <div className="countertop-university-leaf-division"></div>

                    <div className="right">
                        <div className="countertop-university-leaf-right">
                            <CountertopUniversityQuiz 
                                item={{ ...item, videoDone }} 
                                countertopUniversityID={item.countertopUniversityID} 
                                onQuizComplete={onQuizComplete} 
                            />
                        </div>
                    </div>
                </div>
            )}

            {(!item.videoUrl && !item.surveyLink && !item.certificationDone) && <p>Training program coming soon!</p>}

            {item.surveyLink && (
                <div className="survey-center-container">
                    {!item.surveyID ? (
                        <div className="survey-center-content">
                            <p style={{ fontWeight: 500 }}>
                                The survey is not completely set up (surveyID). Please contact your administrator.
                            </p>
                        </div>
                    ) : (
                        <>
                            {!isSurveyStarted && (
                                <div className="survey-center-content">
                                    <p>
                                        Click <b>Start Survey</b> and wait a few seconds for it to load.
                                        When you're done, go back to the Sage Solutions Institute page — you'll see a prompt to generate your certificate.
                                    </p>

                                    {!isSurveyLoading ? (
                                        <Button
                                            icon=""
                                            text="Start survey"
                                            type="default"
                                            width={"200px"}
                                            onClick={handleStartSurvey}
                                        />
                                    ) : (
                                        <div className="survey-loading">
                                            <LoadIndicator height={25} width={25} />
                                            <span>Loading survey...</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div id="smcx-sdk" style={{ margin: "20px" }}></div>
                        </>
                    )}
                </div>
            )}

            {item.certificationDone && (
                <CountertopUniversityCertificateViewerContent
                    certUrl={item.certUrl!}
                    date={formatShortDateShortTime(item.certificateCompletedOn!)}
                    email={item.certificateEmail!}
                />
            )}
        </div>
    );
}